import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  FileText, 
  Search, 
  HelpCircle, 
  ExternalLink, 
  Code, 
  BookOpen, 
  TrendingUp,
  MapPin,
  Calculator,
  Building2,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { LeaderboardAd, InContentAd, RectangleAd } from '@/components/shared/AdSense';
import AdFrame from '@/components/shared/AdFrame';
import SearchDropdown from '@/components/search/SearchDropdown';
import OccupationLink from '@/components/shared/OccupationLink';
import RelatedMetrics from '@/components/shared/RelatedMetrics';
import { generateWebPageSchema, generateOrganizationSchema, generateBreadcrumbSchema, generateMultipleSchemas } from '@/lib/schema';
import { supabase } from '@/integrations/supabase/client';
import { createOccupationSlug } from '@/lib/occupationUtils';

const SocCodeGuide = () => {
  const baseUrl = 'https://wageatlas.com';
  const pageUrl = `${baseUrl}/what-is-soc-code/`;

  // Fetch popular occupations for examples table
  const { data: popularOccupations = [] } = useQuery({
    queryKey: ['popular-occupations-soc-guide'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-occupations', {
        body: { limit: 8 }
      });
      if (error) throw error;
      return (data?.occupations || []).slice(0, 8);
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  // Common SOC codes examples (fallback if API fails)
  const commonSocCodes = [
    { soc: '15-1252', title: 'Software Developer', description: 'Design and develop software applications' },
    { soc: '29-1141', title: 'Registered Nurse', description: 'Provide and coordinate patient care' },
    { soc: '11-2021', title: 'Marketing Manager', description: 'Plan and direct marketing policies' },
    { soc: '15-2051', title: 'Data Scientist', description: 'Analyze and interpret complex data' },
    { soc: '13-2051', title: 'Financial Analyst', description: 'Conduct quantitative investment analyses' },
    { soc: '25-2021', title: 'Elementary School Teacher', description: 'Teach academic and social skills' },
  ];

  const examples = popularOccupations.length > 0 
    ? popularOccupations.map((occ: unknown) => ({
        soc: occ.soc || occ.soc_code || '',
        title: occ.title || occ.occ_title || '',
        description: occ.description || ''
      }))
    : commonSocCodes;

  // FAQ data
  const faqItems = [
    {
      question: "What does SOC stand for?",
      answer: "SOC stands for Standard Occupational Classification. It's a federal classification system developed by the U.S. Bureau of Labor Statistics (BLS) to organize occupations into hierarchical categories for statistical purposes."
    },
    {
      question: "How often are SOC codes updated?",
      answer: "SOC codes are updated periodically by the BLS. The current version is SOC 2018, which replaced SOC 2010. Major updates typically occur every 8-10 years to reflect changes in the labor market and emerging occupations."
    },
    {
      question: "Can I search by SOC code on Wage Atlas?",
      answer: "Yes! You can search by SOC code on Wage Atlas. Simply enter the SOC code (e.g., '15-1252') in the search bar, or search by occupation name. Our system will find the matching occupation and display salary data."
    },
    {
      question: "What's the difference between SOC 2010 and SOC 2018?",
      answer: "SOC 2018 is the current classification system and includes updates to reflect changes in the labor market. Some occupations were reclassified, new occupations were added, and some codes changed. Wage Atlas uses SOC 2018 codes for all current data."
    },
    {
      question: "Where can I find my occupation's SOC code?",
      answer: "You can find your SOC code in several ways: (1) Check job postings - many employers include SOC codes, (2) Use Wage Atlas search to look up your occupation, (3) Visit the official BLS SOC website, or (4) Search by occupation name on Wage Atlas and the SOC code will be displayed."
    },
    {
      question: "Do all jobs have SOC codes?",
      answer: "Most occupations in the U.S. labor market have SOC codes. The system covers over 800 detailed occupations organized into 23 major groups. However, very new or highly specialized roles may not have specific codes yet."
    }
  ];

  // Generate structured data
  const generateStructuredData = () => {
    const breadcrumbs = generateBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Resources', url: '/resources' },
      { name: 'SOC Code Guide', url: '/what-is-soc-code' }
    ], baseUrl);

    const webPageSchema = generateWebPageSchema(
      'What is a SOC Code? Complete Guide 2024 | Wage Atlas',
      'Learn what SOC codes are, how to find your occupation\'s SOC code, and use it to research salaries. Complete guide with examples and lookup tools.',
      pageUrl,
      {
        breadcrumbs: [
          { name: 'Home', url: '/' },
          { name: 'Resources', url: '/resources' },
          { name: 'SOC Code Guide', url: '/what-is-soc-code' }
        ],
        baseUrl
      }
    );

    const organizationSchema = generateOrganizationSchema(baseUrl);

    // Article schema
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'What is a SOC Code? Complete Guide to Standard Occupational Classification',
      description: 'Comprehensive guide explaining SOC codes, how to find them, and how to use them for salary research.',
      author: {
        '@type': 'Organization',
        name: 'Wage Atlas'
      },
      publisher: organizationSchema,
      datePublished: '2024-01-01',
      dateModified: new Date().toISOString().split('T')[0],
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': pageUrl
      }
    };

    // HowTo schema for "How to Find Your SOC Code"
    const howToSchema = {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to Find Your SOC Code',
      description: 'Step-by-step guide to finding your occupation\'s SOC code',
      step: [
        {
          '@type': 'HowToStep',
          name: 'Check Job Postings',
          text: 'Many job postings include SOC codes in the job description or requirements section. Look for a code in the format XX-XXXX.'
        },
        {
          '@type': 'HowToStep',
          name: 'Use Wage Atlas Search',
          text: 'Search for your occupation on Wage Atlas. The SOC code will be displayed on the occupation salary page.'
        },
        {
          '@type': 'HowToStep',
          name: 'Visit BLS Website',
          text: 'Use the official BLS SOC website to browse occupations by category and find your specific code.'
        },
        {
          '@type': 'HowToStep',
          name: 'Search by Occupation Name',
          text: 'Enter your job title in the Wage Atlas search bar. The system will match it to the correct SOC code.'
        }
      ]
    };

    // FAQPage schema
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    };

    return generateMultipleSchemas([
      webPageSchema,
      organizationSchema,
      breadcrumbs,
      articleSchema,
      howToSchema,
      faqSchema
    ]);
  };

  const structuredData = generateStructuredData();

  return (
    <>
      <Helmet>
        <title>What is a SOC Code? Complete Guide 2024 | Wage Atlas</title>
        <meta 
          name="description" 
          content="Learn what SOC codes are, how to find your occupation's SOC code, and use it to research salaries. Complete guide with examples and lookup tools." 
        />
        <meta name="keywords" content="SOC code, Standard Occupational Classification, find SOC code, occupation code, BLS SOC, SOC code lookup" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content="What is a SOC Code? Complete Guide 2024 | Wage Atlas" />
        <meta property="og:description" content="Learn what SOC codes are, how to find your occupation's SOC code, and use it to research salaries." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Wage Atlas" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="What is a SOC Code? Complete Guide 2024 | Wage Atlas" />
        <meta name="twitter:description" content="Learn what SOC codes are, how to find your occupation's SOC code, and use it to research salaries." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {structuredData}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-4 gap-8">
              <div className="lg:col-span-3 space-y-8">
                {/* Breadcrumb */}
                <nav className="text-sm text-muted-foreground" aria-label="Breadcrumb">
                  <ol className="flex items-center space-x-2">
                    <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                    <li>/</li>
                    <li><Link to="/resources" className="hover:text-primary transition-colors">Resources</Link></li>
                    <li>/</li>
                    <li className="text-foreground">SOC Code Guide</li>
                  </ol>
                </nav>

                {/* Hero Section */}
                <div className="text-center space-y-4">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Code className="h-8 w-8 text-primary" />
                    <h1 className="text-3xl md:text-4xl font-bold">What is a SOC Code?</h1>
                  </div>
                  <p className="text-xl text-muted-foreground">
                    Complete Guide to Standard Occupational Classification
                  </p>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    Learn what SOC codes are, why they matter for job seekers, and how to find your occupation's code to research salaries effectively.
                  </p>
                  <div className="flex items-center justify-center gap-4 mt-4">
                    <Badge variant="secondary" className="text-sm">
                      <Code className="h-3 w-3 mr-1" />
                      Example: 15-1252 = Software Developer
                    </Badge>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>Last updated: January 2024</span>
                    </div>
                  </div>
                </div>

                {/* Leaderboard Ad */}
                <AdFrame className="mb-8">
                  <LeaderboardAd />
                </AdFrame>

                {/* What is SOC Code? */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-primary" />
                      What is a SOC Code?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      A <strong>Standard Occupational Classification (SOC)</strong> code is a federal classification system developed by the 
                      U.S. Bureau of Labor Statistics (BLS) to organize occupations into hierarchical categories for statistical analysis 
                      and reporting.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-2">Format</h4>
                        <p className="text-sm text-muted-foreground">
                          SOC codes follow the format <code className="bg-background px-1 rounded">XX-XXXX</code>, where the first two digits 
                          represent the major group and the last four digits represent the detailed occupation.
                        </p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-2">Current Version</h4>
                        <p className="text-sm text-muted-foreground">
                          Wage Atlas uses <strong>SOC 2018</strong> codes for consistent occupation identification across all salary data 
                          and career insights.
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      The SOC system helps standardize how occupations are classified across government agencies, employers, and data 
                      providers, making it easier to compare salary data and employment statistics.
                    </p>
                  </CardContent>
                </Card>

                {/* Why SOC Codes Matter */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      Why SOC Codes Matter
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <h4 className="font-medium mb-2 text-blue-800 dark:text-blue-200">Job Postings</h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Many employers include SOC codes in job postings to help with government reporting and data collection.
                        </p>
                      </div>
                      <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                        <h4 className="font-medium mb-2 text-green-800 dark:text-green-200">Government Agencies</h4>
                        <p className="text-sm text-green-700 dark:text-green-300">
                          Federal and state agencies use SOC codes for labor market analysis, immigration processing, and economic reporting.
                        </p>
                      </div>
                      <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                        <h4 className="font-medium mb-2 text-purple-800 dark:text-purple-200">Salary Data</h4>
                        <p className="text-sm text-purple-700 dark:text-purple-300">
                          Salary data from the BLS and other sources is organized by SOC codes, making it essential for accurate research.
                        </p>
                      </div>
                      <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
                        <h4 className="font-medium mb-2 text-orange-800 dark:text-orange-200">Role Comparison</h4>
                        <p className="text-sm text-orange-700 dark:text-orange-300">
                          SOC codes help you compare similar roles across different industries and geographic locations.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* How to Find Your SOC Code */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Search className="h-5 w-5 text-primary" />
                      How to Find Your SOC Code
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex gap-4 p-4 bg-muted/30 rounded-lg">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-bold">1</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium mb-2">Check Job Postings</h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            Many job postings include SOC codes in the job description or requirements section. Look for a code in the 
                            format <code className="bg-background px-1 rounded">XX-XXXX</code> near the job title or in the job details.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-muted/30 rounded-lg">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-bold">2</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium mb-2">Use Wage Atlas Search</h4>
                          <p className="text-sm text-muted-foreground mb-3">
                            Search for your occupation on Wage Atlas. You can search by job title or directly by SOC code. The SOC code 
                            will be displayed on every occupation salary page.
                          </p>
                          <div className="mt-3">
                            <SearchDropdown placeholder="Search by job title or SOC code..." />
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-muted/30 rounded-lg">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-bold">3</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium mb-2">Visit BLS Website</h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            Use the official BLS SOC website to browse occupations by category and find your specific code. This is the 
                            authoritative source for SOC classifications.
                          </p>
                          <a 
                            href="https://www.bls.gov/soc/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                          >
                            Visit BLS SOC Website <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>

                      <div className="flex gap-4 p-4 bg-muted/30 rounded-lg">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-bold">4</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium mb-2">Search by Occupation Name</h4>
                          <p className="text-sm text-muted-foreground">
                            Type what you call your job and we map it to the official occupation. Everyday titles work:
                            "doctor" finds physicians, "parole officer" finds Probation Officers and Correctional Treatment
                            Specialists, "software engineer" finds Software Developers.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <Link to="/search">
                        <Button className="w-full sm:w-auto">
                          <Search className="h-4 w-4 mr-2" />
                          Search Your Occupation
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>

                {/* In-Content Ad */}
                <InContentAd />

                {/* Understanding SOC Code Structure */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Code className="h-5 w-5 text-primary" />
                      Understanding SOC Code Structure
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      SOC codes are structured hierarchically. Understanding the structure helps you navigate the classification system.
                    </p>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-medium mb-3">Example: 15-1252 (Software Developer)</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">15</Badge>
                          <span className="text-muted-foreground">
                            <strong>Major Group:</strong> Computer and Mathematical Occupations
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">15-1250</Badge>
                          <span className="text-muted-foreground">
                            <strong>Minor Group:</strong> Software Developers and Programmers
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">15-1252</Badge>
                          <span className="text-muted-foreground">
                            <strong>Detailed Occupation:</strong> Software Developers
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                      <div className="p-3 bg-muted/30 rounded-lg text-center">
                        <div className="text-2xl font-bold text-primary">11</div>
                        <div className="text-xs text-muted-foreground mt-1">Management</div>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-lg text-center">
                        <div className="text-2xl font-bold text-primary">15</div>
                        <div className="text-xs text-muted-foreground mt-1">Computer</div>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-lg text-center">
                        <div className="text-2xl font-bold text-primary">29</div>
                        <div className="text-xs text-muted-foreground mt-1">Healthcare</div>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-lg text-center">
                        <div className="text-2xl font-bold text-primary">13</div>
                        <div className="text-xs text-muted-foreground mt-1">Business</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Common SOC Codes */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Common SOC Codes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Here are some popular occupations with their SOC codes. Click on any occupation to view detailed salary data.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 font-medium">SOC Code</th>
                            <th className="text-left p-3 font-medium">Occupation</th>
                            <th className="text-left p-3 font-medium">Description</th>
                            <th className="text-left p-3 font-medium">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {examples.map((example, index) => (
                            <tr key={index} className="border-b hover:bg-muted/30 transition-colors">
                              <td className="p-3">
                                <code className="bg-muted px-2 py-1 rounded text-sm font-mono">{example.soc}</code>
                              </td>
                              <td className="p-3 font-medium">
                                <OccupationLink
                                  occupation={{
                                    title: example.title,
                                    soc: example.soc,
                                    occ_title: example.title
                                  }}
                                  location="united-states"
                                >
                                  {example.title}
                                </OccupationLink>
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">
                                {example.description || 'View salary data for this occupation'}
                              </td>
                              <td className="p-3">
                                <div className="flex flex-col gap-1.5">
                                  <Link 
                                    to={`/salary/${createOccupationSlug(example.title)}/united-states?soc=${example.soc}`}
                                    rel="nofollow"
                                    className="text-primary hover:underline text-sm inline-flex items-center gap-1"
                                    title={`View ${example.title} salary data (SOC: ${example.soc})`}
                                  >
                                    View Salary <ExternalLink className="h-3 w-3" />
                                  </Link>
                                  <Link 
                                    to={`/trends/${createOccupationSlug(example.title)}/united-states?soc=${example.soc}`}
                                    rel="nofollow"
                                    className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                                    title={`View ${example.title} salary trends`}
                                  >
                                    View Trends <ExternalLink className="h-3 w-3" />
                                  </Link>
                                  <Link 
                                    to={`/best-states/${createOccupationSlug(example.title)}?soc=${example.soc}`}
                                    rel="nofollow"
                                    className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                                    title={`Compare ${example.title} across states`}
                                  >
                                    Compare States <ExternalLink className="h-3 w-3" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4">
                      <Link to="/search">
                        <Button variant="outline" className="w-full sm:w-auto">
                          <Search className="h-4 w-4 mr-2" />
                          Search More Occupations
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>

                {/* Using SOC Codes on Wage Atlas */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      Using SOC Codes on Wage Atlas
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Search by SOC Code</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        You can search directly by SOC code on Wage Atlas. Simply enter the code (e.g., "15-1252") in the search bar, 
                        and you'll be taken to the occupation's salary page.
                      </p>
                      <Link to="/search?q=15-1252">
                        <Button variant="outline" size="sm">
                          Try SOC Code Search
                        </Button>
                      </Link>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Interpreting SOC Codes on Salary Pages</h4>
                      <p className="text-sm text-muted-foreground">
                        Every occupation salary page on Wage Atlas displays the SOC code prominently. This helps you verify you're 
                        looking at the correct occupation and makes it easy to reference in job applications or research.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Data Sources</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Our salary data comes from the U.S. Bureau of Labor Statistics (BLS) Occupational Employment and Wage Statistics 
                        (OEWS) program, which uses SOC codes to organize all occupation data.
                      </p>
                      <Link to="/methodology">
                        <Button variant="outline" size="sm">
                          Learn About Our Methodology
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>

                {/* SOC vs NAICS */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      SOC vs. NAICS: Occupations Are Not Industries
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-muted-foreground">
                    <p>
                      The single most common confusion in labor data is mixing up the two federal coding
                      systems. <strong className="text-foreground">SOC codes classify what a person does</strong> — their
                      occupation. <strong className="text-foreground">NAICS codes classify what their employer makes or
                      sells</strong> — the industry. Every worker in America sits at the intersection of one SOC
                      code and one NAICS code, and the two are independent.
                    </p>
                    <p>
                      A worked example makes it concrete. Consider an accountant (SOC 13-2011). She might work at:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-2 text-sm">
                      <li>
                        An accounting firm — NAICS 5412 (Accounting, Tax Preparation, Bookkeeping, and Payroll Services)
                      </li>
                      <li>A hospital — NAICS 622 (Hospitals)</li>
                      <li>A software company — NAICS 5112 (Software Publishers)</li>
                      <li>A school district — NAICS 6111 (Elementary and Secondary Schools)</li>
                    </ul>
                    <p>
                      Same occupation, four different industries — and meaningfully different pay. Software
                      publishers typically pay accountants well above the occupation's overall median; school
                      districts typically pay below it. This is why every Wage Atlas salary page includes an
                      industry breakdown: <em>which industry you practice your occupation in is one of the
                      largest pay levers you control</em>, often worth more than a promotion within a
                      lower-paying industry.
                    </p>
                    <p>
                      Reading the codes themselves is straightforward once you know the pattern. SOC codes are
                      always two digits, a hyphen, then four digits (15-1252), where the first two digits name
                      one of 23 major occupational groups. NAICS codes are two to six digits with no hyphen
                      (541211), where each additional digit narrows the industry: 54 is Professional Services,
                      5412 is Accounting Services, 541211 is Offices of Certified Public Accountants. If a code
                      has a hyphen, it describes a job; if it is a plain string of digits, it describes a
                      business.
                    </p>
                    <p>
                      Explore industry-level wage data for any sector on our{' '}
                      <Link to="/industry" className="text-primary hover:underline">industry pages</Link>, or open any
                      occupation's salary page to see its top-paying industries side by side.
                    </p>
                  </CardContent>
                </Card>

                {/* FAQ Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HelpCircle className="h-5 w-5 text-primary" />
                      Frequently Asked Questions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="single" collapsible className="w-full">
                      {faqItems.map((faq, index) => (
                        <AccordionItem key={index} value={`item-${index}`}>
                          <AccordionTrigger className="text-left font-medium">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-muted-foreground">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>

                {/* Related Resources */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      Related Resources
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Link
                        to="/methodology"
                        className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
                      >
                        <FileText className="h-5 w-5 text-primary" />
                        <div>
                          <div className="font-medium group-hover:text-primary transition-colors">Methodology</div>
                          <div className="text-sm text-muted-foreground">Learn about our data sources</div>
                        </div>
                      </Link>
                      <Link
                        to="/search"
                        className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
                      >
                        <Search className="h-5 w-5 text-primary" />
                        <div>
                          <div className="font-medium group-hover:text-primary transition-colors">Search Occupations</div>
                          <div className="text-sm text-muted-foreground">Find salary data by SOC code</div>
                        </div>
                      </Link>
                      <Link
                        to="/calculators"
                        className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
                      >
                        <Calculator className="h-5 w-5 text-primary" />
                        <div>
                          <div className="font-medium group-hover:text-primary transition-colors">Salary Calculators</div>
                          <div className="text-sm text-muted-foreground">Convert wages and calculate taxes</div>
                        </div>
                      </Link>
                      <Link
                        to="/best-states"
                        className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
                      >
                        <MapPin className="h-5 w-5 text-primary" />
                        <div>
                          <div className="font-medium group-hover:text-primary transition-colors">Best States</div>
                          <div className="text-sm text-muted-foreground">Compare salaries by location</div>
                        </div>
                      </Link>
                    </div>
                  </CardContent>
                </Card>

                {/* Related Metrics for Example Occupations */}
                {examples.length > 0 && examples[0] && (
                  <RelatedMetrics
                    occupation={{
                      title: examples[0].title,
                      occ_title: examples[0].title,
                      soc: examples[0].soc,
                      soc_code: examples[0].soc,
                    }}
                    location="united-states"
                    variant="card"
                    className="mt-6"
                  />
                )}

                {/* CTA Section */}
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="pt-6">
                    <div className="text-center space-y-4">
                      <h3 className="text-2xl font-bold">Ready to Research Salaries?</h3>
                      <p className="text-muted-foreground">
                        Use SOC codes to find accurate salary data for any occupation across all U.S. states and metropolitan areas.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/search">
                          <Button size="lg">
                            <Search className="h-4 w-4 mr-2" />
                            Search Occupations
                          </Button>
                        </Link>
                        <Link to="/ai-career-analysis">
                          <Button variant="outline" size="lg">
                            Get Free Reports
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="hidden lg:block space-y-8">
                <RectangleAd className="sticky top-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SocCodeGuide;
